/**
 * The canonical tax calculation engine.
 *
 * This module is the ONLY place in LaundryPro where VAT is derived. The API,
 * the order screen, the invoice record, the A4 PDF renderer and the thermal
 * receipt renderer all consume the result of these functions. None of them
 * recomputes tax independently — that is how two documents for the same
 * invoice end up disagreeing by one halala.
 *
 * Two rules make the arithmetic reproducible:
 *
 *   1. Rounding happens exactly ONCE per line, at the point where a rate is
 *      applied. Nothing downstream rounds again.
 *   2. Invoice totals are the SUM OF ALREADY-ROUNDED LINES. They are never
 *      recomputed by applying the rate to a subtotal, because sum-of-rounded
 *      and rounded-of-sum differ, and the invoice must equal what its own lines
 *      say it equals.
 */

import {
  BASIS_POINTS_DENOMINATOR,
  MoneyError,
  assertBasisPoints,
  assertMinorUnits,
  assertNonNegative,
  multiplyMinorUnits,
  roundMinorUnits,
  sumMinorUnits,
  type BasisPoints,
  type MinorUnits,
} from "./money.js";

export interface TaxLineInput {
  /** Price of one unit, in minor units. */
  readonly unitPrice: MinorUnits;
  /** May be fractional for weight-priced services (2.5 kg). */
  readonly quantity: number;
  /** Absolute discount in minor units, applied BEFORE tax is derived. */
  readonly discount?: MinorUnits;
  readonly rateBps: BasisPoints;
  /**
   * `true`  — `unitPrice` already contains VAT; VAT is extracted from it.
   * `false` — `unitPrice` excludes VAT; VAT is added on top.
   */
  readonly pricesInclusive: boolean;
}

export interface TaxLineResult {
  /** Line amount excluding VAT. */
  readonly net: MinorUnits;
  /** VAT charged on this line. */
  readonly vat: MinorUnits;
  /** What the customer pays for this line. Always exactly `net + vat`. */
  readonly total: MinorUnits;
  /** The discount actually applied, echoed back for the invoice record. */
  readonly discount: MinorUnits;
  readonly rateBps: BasisPoints;
}

export interface TaxRateBreakdown {
  readonly rateBps: BasisPoints;
  readonly net: MinorUnits;
  readonly vat: MinorUnits;
  readonly total: MinorUnits;
}

export interface TaxTotals {
  readonly net: MinorUnits;
  readonly vat: MinorUnits;
  readonly total: MinorUnits;
  readonly discount: MinorUnits;
  /**
   * Per-rate breakdown, ascending by rate. A Saudi tax invoice must show VAT
   * grouped by rate when an invoice mixes standard-rated and zero-rated lines.
   */
  readonly byRate: readonly TaxRateBreakdown[];
}

/**
 * Computes one line.
 *
 * Discount is subtracted from the line amount before VAT is derived, because a
 * discount reduces the consideration actually received, and VAT is due on
 * consideration received — not on a list price the customer never paid.
 */
export function computeLineTax(input: TaxLineInput): TaxLineResult {
  const { unitPrice, quantity, rateBps, pricesInclusive } = input;
  const discount = input.discount ?? 0;

  assertNonNegative(unitPrice, "unitPrice");
  assertNonNegative(discount, "discount");
  assertBasisPoints(rateBps, "rateBps");

  if (typeof quantity !== "number" || !Number.isFinite(quantity) || quantity < 0) {
    throw new MoneyError(`quantity must be a finite non-negative number, received ${String(quantity)}`);
  }

  // A zero quantity is legitimate: a line can be emptied on screen before the
  // order is submitted. It is not treated as an error, only as a zero line.
  const lineAmount = multiplyMinorUnits(unitPrice, quantity);

  if (discount > lineAmount) {
    throw new MoneyError(
      `discount (${discount}) exceeds the line amount (${lineAmount}); a line cannot have a negative base`,
    );
  }

  const taxable = lineAmount - discount;

  if (pricesInclusive) {
    // `taxable` already contains VAT. Extract the net, then derive VAT by
    // SUBTRACTION rather than by a second rounding — this is what guarantees
    // net + vat === total exactly, for every price, at every rate.
    const net = roundMinorUnits((taxable * BASIS_POINTS_DENOMINATOR) / (BASIS_POINTS_DENOMINATOR + rateBps));
    const vat = taxable - net;
    return { net, vat, total: taxable, discount, rateBps };
  }

  // `taxable` excludes VAT. Round the VAT once; the total follows by addition.
  const vat = roundMinorUnits((taxable * rateBps) / BASIS_POINTS_DENOMINATOR);
  const total = taxable + vat;
  assertMinorUnits(total, "line total");
  return { net: taxable, vat, total, discount, rateBps };
}

/**
 * Aggregates already-computed lines into invoice totals.
 *
 * Takes `TaxLineResult`s, not raw inputs, so it is structurally impossible for
 * an invoice total to be derived from anything other than the same rounded line
 * values that the invoice prints.
 */
export function aggregateTax(lines: readonly TaxLineResult[]): TaxTotals {
  const net = sumMinorUnits(lines.map((l) => l.net));
  const vat = sumMinorUnits(lines.map((l) => l.vat));
  const total = sumMinorUnits(lines.map((l) => l.total));
  const discount = sumMinorUnits(lines.map((l) => l.discount));

  const perRate = new Map<BasisPoints, { net: MinorUnits; vat: MinorUnits; total: MinorUnits }>();
  for (const line of lines) {
    const bucket = perRate.get(line.rateBps) ?? { net: 0, vat: 0, total: 0 };
    perRate.set(line.rateBps, {
      net: bucket.net + line.net,
      vat: bucket.vat + line.vat,
      total: bucket.total + line.total,
    });
  }

  const byRate: TaxRateBreakdown[] = [...perRate.entries()]
    .sort((a, b) => a[0] - b[0])
    .map(([rateBps, sums]) => ({ rateBps, net: sums.net, vat: sums.vat, total: sums.total }));

  return { net, vat, total, discount, byRate };
}

/** Convenience: compute a whole document in one call. */
export function computeDocumentTax(inputs: readonly TaxLineInput[]): {
  readonly lines: readonly TaxLineResult[];
  readonly totals: TaxTotals;
} {
  const lines = inputs.map(computeLineTax);
  return { lines, totals: aggregateTax(lines) };
}

/** Renders a basis-point rate for display: 1500 → "15%", 525 → "5.25%". */
export function formatRatePercent(rateBps: BasisPoints): string {
  assertBasisPoints(rateBps);
  const whole = Math.trunc(rateBps / 100);
  const fraction = rateBps % 100;
  if (fraction === 0) return `${whole}%`;
  const trimmed = String(fraction).padStart(2, "0").replace(/0$/, "");
  return `${whole}.${trimmed}%`;
}

/** The standard Saudi VAT rate at the time of writing. Configurable per install. */
export const DEFAULT_VAT_RATE_BPS: BasisPoints = 1500;
